/* ==========================================================================
   we-pdp-cart.js - New PDP Quantity + Add to Cart.
   --------------------------------------------------------------------------
   Owns the two things that actually change Shopify's cart: the quantity
   stepper and the two Add to Cart buttons (main buy-box + sticky bar).
   Both buttons submit the SAME purchase state - there is exactly one
   quantity value (this file's own state.quantity) and exactly one
   resolved variant/mode/selling-plan (window.WePdpPricing.getPurchaseState(),
   assets/we-pdp-pricing.js's own tracked state - not re-resolved here).

   Cart request: a plain fetch to /cart/add.js with the native Shopify
   Ajax Cart API JSON body - {items: [{id, quantity, selling_plan}]},
   selling_plan omitted entirely for a one-time purchase (Shopify treats a
   present-but-undefined key and an absent key the same, but the key is
   left out here rather than sent as null/blank, so there is no ambiguity
   in what gets POSTed). This is a deliberate, minimal departure from the
   legacy theme's own /cart/add.js caller (assets/theme.js's
   actionAddToCart2, which serializes a real <form> as FormData): the New
   PDP's variant/quantity/selling-plan are already JS-tracked state, not
   form fields, so building the JSON body directly is simpler than
   constructing a form only to immediately re-serialize it - and it is the
   exact request shape Shopify's Ajax Cart API expects, so the resulting
   cart line is identical either way.

   Cart success reveal: this shop is configured to the existing sidebar
   cart drawer (window.after_add_to_cart.type === "quick_cart",
   #halo-cart-sidebar already present in the global layout on every
   template, this one included). The theme's own trigger for it,
   halo.updateSidebarCart(), lives on a "halo" global that this file found
   NOT reliably present on this template (theme.js throws before finishing
   its own setup here - a pre-existing, unrelated issue), so calling
   window.halo.updateSidebarCart() outright would be fragile. This file
   calls it when available (the most faithful "reuse the existing
   mechanism" outcome), and otherwise reproduces its own two effects
   directly: GET /cart?view=ajax_side_cart (the same server-rendered
   Liquid partial the theme already uses) into the drawer's own
   .previewCart-wrapper, and add the same "cart-sidebar-show" body class
   the theme's own CSS already keys off. That GET's response is Shopify's
   own first-party server-rendered HTML for this store (not a string this
   file assembles from raw cart JSON), so assigning it via innerHTML
   reproduces the theme's own established drawer-refresh mechanism rather
   than hand-building cart markup - every OTHER piece of dynamic text this
   file ever displays (quantity, button labels, error messages) goes
   through textContent only, never innerHTML.

   Own idempotent bind (window.WePdpCart), own delegated listeners - same
   constraints as the other we-pdp-*.js files.
   ========================================================================== */
(function () {
  'use strict';

  if (window.WePdpCart && window.WePdpCart.bound) {
    window.WePdpCart.refresh();
    return;
  }

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ------------------------------------------------------------------------
     Quantity
     ------------------------------------------------------------------------
     One quantity value for the whole page - the main input is the only
     visible control (no quantity UI exists on the sticky bar in the
     approved design; it always submits whatever the main input holds).
     Constraints come from the currently resolved variant's own
     quantity_rule (Shopify-native min/max/increment - see
     we-pdp-buybox.liquid's JSON), never an invented cap.
     ------------------------------------------------------------------------ */
  var quantity = 1;
  var qtyRule = { min: 1, max: null, increment: 1 };

  function clampQuantity(value) {
    var n = parseInt(value, 10);
    if (isNaN(n)) n = qtyRule.min;
    var step = qtyRule.increment || 1;
    if (n < qtyRule.min) n = qtyRule.min;
    if (qtyRule.max && n > qtyRule.max) n = qtyRule.max;
    /* Snap to the nearest valid increment above the minimum, same
       arithmetic the browser's own stepUp/stepDown already does for
       <input type="number" step="...">. */
    var offset = n - qtyRule.min;
    if (step > 1 && offset % step !== 0) {
      n = qtyRule.min + Math.round(offset / step) * step;
    }
    return n;
  }

  /* Fires only when the clamped value actually changes, same
     dispatch-on-change (not dispatch-on-every-call) discipline
     we-pdp-variants.js's selectVariant() already uses for
     "wep:variant-change" - assets/we-pdp-pricing.js listens for this to
     re-render the CTA price for the new quantity, without this file
     needing to know anything about how price is displayed. */
  function setQuantity(value, opts) {
    var next = clampQuantity(value);
    var changed = next !== quantity;
    quantity = next;
    var input = qs('[data-wep-qty-input]');
    if (input) input.value = String(quantity);
    if (!opts || !opts.silent) syncQuantityButtons();
    if (changed) {
      document.dispatchEvent(new CustomEvent('wep:quantity-change', { detail: { quantity: quantity } }));
    }
  }

  function syncQuantityButtons() {
    var minusBtn = qs('[data-wep-qty-minus]');
    if (minusBtn) minusBtn.disabled = quantity <= qtyRule.min;
    var plusBtn = qs('[data-wep-qty-plus]');
    if (plusBtn) plusBtn.disabled = !!(qtyRule.max && quantity >= qtyRule.max);
  }

  function applyQtyRuleFromVariant(entry) {
    var rule = (entry && entry.qty) || {};
    qtyRule = {
      min: rule.min || 1,
      max: rule.max || null,
      increment: rule.increment || 1
    };
    /* qtyRule (this module's own state) is the only place min/max/increment
       live - the input is type="text" (see its markup comment), so there
       is no native min/max/step attribute to keep in sync with it; setting
       one would be inert HTML with nothing enforcing it, i.e. a second,
       misleading source of truth. */

    /* Re-clamp the current quantity against the new variant's rule (e.g.
       a lower max on the newly selected variant) rather than silently
       submitting a now-invalid value. */
    setQuantity(quantity);
  }

  function onQtyStep(delta) {
    setQuantity(quantity + delta * (qtyRule.increment || 1));
  }

  /* Fires on every keystroke (input event) - only strips non-digits, so
     the field never visibly shows a letter/symbol the shopper typed, but
     deliberately does NOT clamp to min/max yet (an in-progress "" or "0"
     while the shopper is still typing a longer number, e.g. typing "10" by
     first passing through "1", must not get force-corrected mid-keystroke).
     clampQuantity()'s own isNaN() guard already makes an empty value safe
     to leave on screen momentarily. */
  function onQtyInputTyping(input) {
    var digitsOnly = input.value.replace(/[^0-9]/g, '');
    if (digitsOnly !== input.value) input.value = digitsOnly;
  }

  /* Fires on blur/change (focus leaving the field, or Enter) - this is
     where an in-progress/empty value actually gets normalized against
     qtyRule and written back, same as clicking a +/- button. */
  function onQtyInputChange(input) {
    setQuantity(input.value);
  }

  /* ------------------------------------------------------------------------
     Variant index (read-only second reader, same payload we-pdp-pricing.js
     already parses - not a second resolver).
     ------------------------------------------------------------------------ */
  var variantsById = {};

  function loadVariants() {
    variantsById = {};
    var script = qs('[data-wep-variant-index]');
    if (!script) return;
    var variants;
    try {
      variants = JSON.parse(script.textContent) || [];
    } catch (e) {
      variants = [];
    }
    variants.forEach(function (v) {
      variantsById[v.id] = v;
    });
  }

  /* ------------------------------------------------------------------------
     Add to Cart buttons - availability/label sync
     ------------------------------------------------------------------------ */
  function atcButtons() {
    return qsa('[data-wep-add-to-cart]');
  }

  function setButtonLabel(button, text) {
    var label = qs('[data-wep-out]', button);
    if (label) label.textContent = text;
  }

  function syncAvailability(entry) {
    var available = !entry || entry.available !== false;
    atcButtons().forEach(function (button) {
      button.disabled = !available;
      setButtonLabel(button, available ? 'Add to Cart' : 'Sold Out');
    });
  }

  /* ------------------------------------------------------------------------
     Loading state
     ------------------------------------------------------------------------
     Both buttons disable together so a submission started from one entry
     point can't be duplicated from the other while it is in flight.
     ------------------------------------------------------------------------ */
  var submitting = false;

  function setLoading(isLoading) {
    submitting = isLoading;

    if (isLoading) {
      atcButtons().forEach(function (button) {
        button.disabled = true;
        setButtonLabel(button, 'Adding…');
      });
      return;
    }

    /* The resolved variant can change while a request is in flight (the
       shopper clicked a different option mid-submit) - re-sync against
       whatever is CURRENTLY resolved rather than assuming the button
       should simply go back to enabled/"Add to Cart". */
    var purchase = window.WePdpPricing && window.WePdpPricing.getPurchaseState
      ? window.WePdpPricing.getPurchaseState()
      : null;
    syncAvailability(purchase ? variantsById[purchase.variantId] : null);
  }

  /* ------------------------------------------------------------------------
     Cart drawer reveal (see file header comment for why this isn't a
     direct halo.updateSidebarCart() call).
     ------------------------------------------------------------------------ */
  function openCartDrawer() {
    if (window.halo && typeof window.halo.updateSidebarCart === 'function') {
      window.halo.updateSidebarCart();
      return Promise.resolve();
    }

    return fetch(routesRoot() + 'cart?view=ajax_side_cart', { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) return;
        return response.text().then(function (html) {
          var wrapper = qs('#halo-cart-sidebar .previewCart-wrapper');
          if (wrapper) wrapper.innerHTML = html;
        });
      })
      .catch(function () {
        /* The item is already in the cart at this point - a failed drawer
           refresh must not read as a failed Add to Cart. */
      })
      .then(function () {
        document.body.classList.add('cart-sidebar-show');
      });
  }

  /* ------------------------------------------------------------------------
     Error surfacing - reuses we-pdp.js's existing toast (already used for
     this page's other placeholder confirmations), textContent only.
     ------------------------------------------------------------------------ */
  function showError(message) {
    if (window.WePdp && typeof window.WePdp.showToast === 'function') {
      window.WePdp.showToast(message);
    }
  }

  /* ------------------------------------------------------------------------
     Add to Cart submission
     ------------------------------------------------------------------------ */
  function addToCart() {
    if (submitting) return;
    if (!window.WePdpPricing || typeof window.WePdpPricing.getPurchaseState !== 'function') return;

    var purchase = window.WePdpPricing.getPurchaseState();
    if (!purchase || !purchase.variantId || purchase.available === false) return;

    /* Defensive re-normalization: a blur/change on the quantity field
       isn't guaranteed to have fired before this click (e.g. a screen
       reader or assistive input path that clicks without blurring first),
       so re-derive quantity from the field's current text one more time
       rather than trusting that a prior event already ran. Still the same
       setQuantity()/clampQuantity() this module already uses everywhere
       else - not a second validation path. */
    var qtyInput = qs('[data-wep-qty-input]');
    if (qtyInput) setQuantity(qtyInput.value);

    var item = { id: purchase.variantId, quantity: quantity };
    if (purchase.mode === 'sub' && purchase.planId) {
      item.selling_plan = purchase.planId;
    }

    setLoading(true);

    fetch(routesRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var message = (result.data && (result.data.description || result.data.message)) || 'Could not add this item to your cart.';
          throw new Error(message);
        }
        return openCartDrawer();
      })
      .then(function () {
        setLoading(false);
      })
      .catch(function (err) {
        setLoading(false);
        showError(err && err.message ? err.message : 'Could not add this item to your cart.');
      });
  }

  /* ------------------------------------------------------------------------
     Variant change - re-evaluate quantity rule + sold-out state for the
     newly resolved variant. Same event we-pdp-variants.js already
     dispatches; this file never resolves a variant itself.
     ------------------------------------------------------------------------ */
  function onVariantChange(event) {
    var variant = event && event.detail && event.detail.variant;
    if (!variant) return;
    var entry = variantsById[variant.id] || variant;
    applyQtyRuleFromVariant(entry);
    syncAvailability(entry);
  }

  /* ------------------------------------------------------------------------
     Delegated events - bound once
     ------------------------------------------------------------------------ */
  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var minus = target.closest('[data-wep-qty-minus]');
    if (minus) {
      onQtyStep(-1);
      return;
    }

    var plus = target.closest('[data-wep-qty-plus]');
    if (plus) {
      onQtyStep(1);
      return;
    }

    var atc = target.closest('[data-wep-add-to-cart]');
    if (atc) {
      event.preventDefault();
      addToCart();
      return;
    }
  }

  function onChange(event) {
    var qtyInput = event.target.closest && event.target.closest('[data-wep-qty-input]');
    if (qtyInput) onQtyInputChange(qtyInput);
  }

  function onInput(event) {
    var qtyInput = event.target.closest && event.target.closest('[data-wep-qty-input]');
    if (qtyInput) onQtyInputTyping(qtyInput);
  }

  /* ------------------------------------------------------------------------
     Init / refresh
     ------------------------------------------------------------------------ */
  function initialVariantId() {
    var root = qs('[data-wep-initial-variant-id]');
    var id = root ? parseInt(root.dataset.wepInitialVariantId, 10) : NaN;
    return isNaN(id) ? null : id;
  }

  function refresh() {
    loadVariants();
    var entry = variantsById[initialVariantId()];
    applyQtyRuleFromVariant(entry);
    syncAvailability(entry);
  }

  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange);
  document.addEventListener('input', onInput);
  document.addEventListener('wep:variant-change', onVariantChange);
  document.addEventListener('shopify:section:load', refresh);

  /* getQuantity(): assets/we-pdp-pricing.js's single read of "how many is
     the shopper buying" - not a second quantity store, just exposing the
     one this file already tracks (same pattern as
     window.WePdpPricing.getPurchaseState() elsewhere in the New PDP). */
  window.WePdpCart = { bound: true, refresh: refresh, getQuantity: function () { return quantity; } };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
